import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import type { SortEvent, MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { SvgIconComponent } from '../../../shared/svg-icon/svg-icon.component';
import { FileItem } from '../../../models';
import { getFileIcon } from '../file-type.utils';
import { getVoucherStatusColor } from '../voucher-status.utils';

@Component({
  selector: 'app-file-list-view',
  standalone: true,
  imports: [CommonModule, FormsModule, TableModule, SvgIconComponent, Menu],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './file-list-view.component.html',
  styleUrl: './file-list-view.component.scss',
})
export class FileListView {
  @Input() files: FileItem[] = [];
  @Input() selectedFiles: FileItem[] = [];
  @Output() selectedFilesChange = new EventEmitter<FileItem[]>();

  @Input() hasSelectedCategory = false;
  @Input() linkStatusMap: Map<string, string[]> = new Map();
  readonly highlightedFileName = input<string | null>(null);

  @Output() navigateToFolder = new EventEmitter<FileItem>();
  @Output() linkSelected = new EventEmitter<void>();
  @Output() showLinks = new EventEmitter<FileItem>();
  @Output() deleteSelected = new EventEmitter<void>();
  @Output() deleteItem = new EventEmitter<FileItem>();
  @Output() previewFile = new EventEmitter<FileItem>();
  @Output() downloadFile = new EventEmitter<FileItem>();
  @Output() renameFile = new EventEmitter<{ file: FileItem; newName: string }>();
  @Output() fileDragStart = new EventEmitter<{ event: DragEvent; file: FileItem }>();

  editingFile: FileItem | null = null;
  editingName = '';

  private readonly editInput = viewChild<ElementRef<HTMLInputElement>>('editInput');
  private readonly rowMenu = viewChild<Menu>('rowMenu');
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);

  rowMenuItems: MenuItem[] = [];

  constructor() {
    effect(() => {
      const el = this.editInput()?.nativeElement;
      const file = this.editingFile;
      if (!el || !file) return;
      el.focus();
      const dotIndex = file.name.lastIndexOf('.');
      const selectionEnd = file.type === 'file' && dotIndex > 0 ? dotIndex : file.name.length;
      el.setSelectionRange(0, selectionEnd);
    });

    effect(() => {
      const name = this.highlightedFileName();
      if (!name) return;
      requestAnimationFrame(() => {
        const row = this.host.nativeElement.querySelector(`tr[data-filename="${CSS.escape(name)}"]`);
        row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  getFileIcon(extension: string): string {
    return getFileIcon(extension);
  }

  getDisplayName(file: FileItem): string {
    if (file.type !== 'file') return file.name;
    const dotIndex = file.name.lastIndexOf('.');
    return dotIndex > 0 ? file.name.slice(0, dotIndex) : file.name;
  }

  getLinkStatuses(fileName: string): string[] {
    return this.linkStatusMap.get(fileName) ?? [];
  }

  getVoucherStatusColor(status: string): string {
    return getVoucherStatusColor(status);
  }

  onSelectedFilesChange(value: FileItem[]): void {
    this.selectedFiles = value;
    this.selectedFilesChange.emit(value);
  }

  startEdit(file: FileItem): void {
    if (file.isScanning) return;
    this.editingFile = file;
    this.editingName = file.name;
    this.cdr.markForCheck();
  }

  cancelEdit(): void {
    this.editingFile = null;
    this.editingName = '';
  }

  confirmEdit(file: FileItem): void {
    const newName = this.editingName.trim();
    if (newName && newName !== file.name) {
      this.renameFile.emit({ file, newName });
    }
    this.cancelEdit();
  }

  customSort(event: SortEvent): void {
    event.data?.sort((a: FileItem, b: FileItem) => {
      if (a.name === '..') return -1;
      if (b.name === '..') return 1;
      if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
      const field = event.field as keyof FileItem;
      return (event.order ?? 1) * String(a[field] ?? '').localeCompare(String(b[field] ?? ''), 'hu');
    });
  }

  get canLinkSelected(): boolean {
    return this.hasSelectedCategory && this.selectedFiles.length > 0;
  }

  get linkSelectedTitle(): string {
    if (this.selectedFiles.length === 0) return 'Nincs kiválasztott fájl';
    if (!this.hasSelectedCategory) return 'Nincs kiválasztott kategória';
    return 'Kijelöltek csatolása';
  }

  get canDeleteSelected(): boolean {
    return this.selectedFiles.length > 0;
  }

  canPreview(file: FileItem): boolean {
    return file.type === 'file' && !file.isScanning;
  }

  onRowClick(file: FileItem): void {
    if (this.canPreview(file)) {
      this.previewFile.emit(file);
    }
  }

  openRowMenu(event: MouseEvent, file: FileItem): void {
    this.rowMenuItems = [
      {
        label: 'Megtekintés',
        icon: 'pi pi-eye',
        disabled: !this.canPreview(file),
        command: () => this.previewFile.emit(file),
      },
      {
        label: 'Átnevezés',
        icon: 'pi pi-pencil',
        disabled: file.isScanning,
        command: () => this.startEdit(file),
      },
      {
        label: 'Letöltés',
        icon: 'pi pi-download',
        disabled: file.isScanning || file.type !== 'file',
        command: () => this.downloadFile.emit(file),
      },
    ];
    this.rowMenu()?.toggle(event);
  }
}
